import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  SegmentedControl,
} from '@mlm/ui';
import { AR_PERMS } from '@mlm/shared';
import { listArInvoices, type ArInvoiceRow } from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';
import { PageHeader } from './_shared';
import { ArInvoiceEditorDialog } from './ar-invoice-editor';
import { RecordPaymentDialog } from './ar-receipts';

/**
 * Billings — general AR invoicing (R2 Phase C).
 *
 * "Billing Statement" is the document's name everywhere in this product;
 * "Invoice" is the BIR-registered statutory name and is reserved for the
 * separate compliance track.
 */

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
] as const;

function peso(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  return `₱${(Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function StatusBadge({ row }: { row: ArInvoiceRow }) {
  if (row.status === 'void') return <Badge variant="muted">Void</Badge>;
  if (row.status === 'draft') return <Badge variant="warning">Draft</Badge>;
  if (row.status === 'paid') return <Badge variant="success">Paid</Badge>;
  // Overdue is derived server-side from the due date, never stored.
  if (row.overdue) return <Badge variant="destructive">Overdue</Badge>;
  return <Badge variant="secondary">{row.status === 'partial' ? 'Partial' : 'Open'}</Badge>;
}

export function ArInvoicesPage() {
  const canWrite = useHasPerm(AR_PERMS.invoiceWrite);
  const canReceive = useHasPerm(AR_PERMS.receiptWrite);
  const [tab, setTab] = useState<string>('all');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState<ArInvoiceRow | null>(null);

  const listQ = useQuery({
    queryKey: ['ar-invoices', tab, q],
    queryFn: () =>
      listArInvoices({
        status: tab === 'all' ? undefined : tab,
        q: q.trim() || undefined,
      }),
  });
  const rows = listQ.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Billings"
        subtitle="Bill a customer for anything — goods, services or a one-off charge. A billing is a draft until you post it; posting is what puts it on the books."
        actions={
          canWrite ? <Button onClick={() => setCreating(true)}>New billing</Button> : undefined
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <SegmentedControl
            value={tab}
            onChange={(v) => setTab(String(v))}
            options={TABS.map((t) => ({ value: t.value as string, label: t.label as string }))}
          />
          <div className="w-64 space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="ar-search">
              Search
            </label>
            <Input
              id="ar-search"
              className="h-9"
              placeholder="Doc number, reference or customer"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">No billings here yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Doc no</th>
                    <th className="px-4 py-2">Customer</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">Due</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-right">Balance</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                      onClick={() => setEditing(r.id)}
                    >
                      <td className="px-4 py-2 font-mono text-xs">{r.docNo}</td>
                      <td className="px-4 py-2">{r.customerName}</td>
                      <td className="px-4 py-2">{shortDate(r.invoiceDate)}</td>
                      <td className="px-4 py-2">{shortDate(r.dueDate)}</td>
                      <td className="px-4 py-2">
                        <StatusBadge row={r} />
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {peso(r.total)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {r.status === 'void' ? '—' : peso(Number(r.total) - Number(r.amountPaid))}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {canReceive && (r.status === 'open' || r.status === 'partial') ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              // The row itself opens the editor.
                              e.stopPropagation();
                              setPaying(r);
                            }}
                          >
                            Record payment
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {creating ? (
        <ArInvoiceEditorDialog invoiceId={null} onClose={() => setCreating(false)} />
      ) : null}
      {editing ? (
        <ArInvoiceEditorDialog invoiceId={editing} onClose={() => setEditing(null)} />
      ) : null}
      {paying ? (
        <RecordPaymentDialog
          invoiceId={paying.id}
          customerId={paying.customerId}
          outstanding={Number(paying.total) - Number(paying.amountPaid)}
          onClose={() => setPaying(null)}
          onDone={() => {
            setPaying(null);
            void listQ.refetch();
          }}
        />
      ) : null}
    </div>
  );
}
